import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { UserButton, OrganizationSwitcher, useUser, useOrganization, useClerk } from '@clerk/clerk-react';
import { 
  FiGrid, 
  FiChevronLeft,
  FiChevronRight,
  FiMonitor,
  FiUsers,
  FiKey,
  FiAlertTriangle,
  FiX,
  FiHome,
  FiMenu,
  FiLogOut
} from 'react-icons/fi';
import { IoMdCube } from 'react-icons/io';
import elsaiLogo from '../elsai.png'; // Import the ELSai logo
import useClerkRedirects from '../hooks/useClerkRedirects';
import AlwaysVisibleFloatingButton from './AlwaysVisibleFloatingButton';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { signOut } = useClerk();
  const [collapsed, setCollapsed] = useState(false);
  const [showNavWarning, setShowNavWarning] = useState(false);
  const [targetRoute, setTargetRoute] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Initialize Clerk redirect handling
  useClerkRedirects();

  // Handle mobile, tablet, and desktop detection
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      
      // Close mobile menu if screen size is no longer mobile
      if (width >= 768) {
        setMobileMenuOpen(false);
      }
      
      // Auto-collapse sidebar on tablet for better space utilization
      if (width >= 768 && width < 1024) {
        setCollapsed(true);
      } else if (width >= 1024) {
        setCollapsed(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const isActive = (path) => {
    return location.pathname.includes(path);
  };

  // Helper function to convert route paths to display names
  const getRouteDisplayName = (route) => {
    if (route.includes('/dashboard')) return 'Dashboard';
    if (route.includes('/projects')) {
      if (route.includes('/runhistory')) return 'Run History';
      if (route.includes('/projects/') && !route.includes('/runhistory')) return 'Project Details';
      return 'Projects';
    }
    if (route.includes('/system-resources')) return 'System Resources';
    if (route.includes('/cost-analytics')) return 'Cost Analytics';
    if (route.includes('/query-analytics')) return 'Query Analytics';
    if (route.includes('/log-analytics')) return 'Log Analytics';
    if (route.includes('/api-keys')) return 'API Keys';
    return route; // fallback to original route if no match
  };

  const handleNavigation = async (path) => {
    // Check if route requires organization
    const protectedRoutes = ['/dashboard', '/projects', '/system-resources', '/cost-analytics', '/query-analytics', '/log-analytics', '/api-keys'];
    const isProtectedRoute = protectedRoutes.some(route => path.startsWith(route));
    
    if (isProtectedRoute && !organization) {
      // Check if user has any organizations
      try {
        if (user) {
          const memberships = await user.getOrganizationMemberships();
          const hasOrgs = memberships.data && memberships.data.length > 0;
          
          if (!hasOrgs) {
            setTargetRoute(path);
            setShowNavWarning(true);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking user organizations:', error);
        setTargetRoute(path);
        setShowNavWarning(true);
        return;
      }
    }
    
    // If we get here, navigation is allowed
    navigate(path);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`bg-white border-r border-gray-200 transition-all duration-300 relative ${
        isMobile 
          ? (mobileMenuOpen ? 'fixed inset-y-0 left-0 w-64 z-50' : 'hidden')
          : isTablet
          ? (collapsed ? 'w-16' : 'w-48')
          : (collapsed ? 'w-20' : 'w-60')
      }`}>
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src={elsaiLogo} 
              alt="ELSai Logo" 
              className={`${collapsed && !isMobile && !isTablet ? 'w-8 h-8' : 'w-10 h-10'}`} 
            />
            {(!collapsed || isMobile) && (
              <span className="ml-2 font-dm-serif text-gray-800 text-2xl">
                ARMS
              </span>
            )}
          </div>
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <FiX size={20} />
            </button>
          )}
        </div>
        
        <nav className="mt-6">
          <button
            onClick={() => {
              handleNavigation('/dashboard');
              if (isMobile) setMobileMenuOpen(false);
            }}
            className={`w-full text-left py-3 px-4 flex items-center ${isActive('/dashboard') ? 
            'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
            <FiGrid className={`${collapsed && !isMobile && !isTablet ? 'mx-auto' : 'mr-3'}`} size={20} />
            {(!collapsed || isMobile) && <span>Dashboard</span>}
          </button>
          
          <button
            onClick={() => {
              handleNavigation('/projects');
              if (isMobile) setMobileMenuOpen(false);
            }}
            className={`w-full text-left py-3 px-4 flex items-center ${isActive('/projects') ? 
            'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
            <IoMdCube className={`${collapsed && !isMobile && !isTablet ? 'mx-auto' : 'mr-3'}`} size={20} />
            {(!collapsed || isMobile) && <span>Projects</span>}
          </button>
          
          <button
            onClick={() => {
              handleNavigation('/system-resources');
              if (isMobile) setMobileMenuOpen(false);
            }}
            className={`w-full text-left py-3 px-4 flex items-center ${isActive('/system-resources') ? 
            'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
            <FiMonitor className={`${collapsed && !isMobile && !isTablet ? 'mx-auto' : 'mr-3'}`} size={20} />
            {(!collapsed || isMobile) && <span>System Resources</span>}
          </button>
          
          <Link 
            to="/organization" 
            onClick={() => isMobile && setMobileMenuOpen(false)}
            className={`py-3 px-4 flex items-center ${isActive('/organization') ? 
            'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
            <FiUsers className={`${collapsed && !isMobile && !isTablet ? 'mx-auto' : 'mr-3'}`} size={20} />
            {(!collapsed || isMobile) && <span>Organizations</span>}
          </Link>
          
          <button
            onClick={() => {
              handleNavigation('/api-keys');
              if (isMobile) setMobileMenuOpen(false);
            }}
            className={`w-full text-left py-3 px-4 flex items-center ${isActive('/api-keys') ? 
            'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
            <FiKey className={`${collapsed && !isMobile && !isTablet ? 'mx-auto' : 'mr-3'}`} size={20} />
            {(!collapsed || isMobile) && <span>API Keys</span>}
          </button>
        </nav>

        {/* Sign Out Button - positioned at bottom */}
        <div className="absolute bottom-4 left-0 right-0 px-4">
          <button
            onClick={() => signOut()}
            className="w-full text-left py-3 px-4 flex items-center text-gray-500 hover:bg-gray-100 hover:text-red-600 transition-colors"
          >
            <FiLogOut className={`${collapsed && !isMobile && !isTablet ? 'mx-auto' : 'mr-3'}`} size={20} />
            {(!collapsed || isMobile) && <span>Sign Out</span>}
          </button>
        </div>

        {/* Sidebar collapse/expand button - show on desktop and tablet */}
        {!isMobile && (
          <button 
            onClick={() => setCollapsed(!collapsed)} 
            className="absolute -right-3 top-20 bg-white border border-gray-200 rounded-full p-1 text-gray-500 hover:text-gray-700 shadow-sm"
          >
            {collapsed ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        {isMobile && (
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <FiMenu size={20} />
            </button>
            <div className="flex items-center space-x-2">
              <img src={elsaiLogo} alt="ELSai Logo" className="w-8 h-8" />
              <span className="font-dm-serif text-gray-800 text-lg">ARMS</span>
            </div>
            <div className="flex items-center space-x-2">
              <UserButton />
            </div>
          </div>
        )}

        {/* Always Visible Floating Warning Button */}
        <AlwaysVisibleFloatingButton />
        
        {/* Navigation Warning Modal */}
        {showNavWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <FiAlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800">Access Restricted</h2>
                    <p className="text-sm text-gray-500">Organization membership required</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNavWarning(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiUsers className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Organization Required
                  </h3>
                  <p className="text-gray-600 mb-4">
                    You need to be part of an organization to access <strong>{getRouteDisplayName(targetRoute)}</strong>.
                  </p>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FiHome className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">
                      You'll be redirected to Organizations
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Page Title Header - Desktop only */}
        {!isMobile && (
          <div className="bg-white border-b border-gray-200 h-16 flex items-center px-6 justify-between">
            <h1 className="text-xl font-semibold text-gray-700">
              {location.pathname.includes('/dashboard') && 'Dashboard'}
              {location.pathname === '/projects' && 'Projects'}
              {location.pathname.includes('/projects/') && !location.pathname.includes('/runhistory') && <span className="font-bold">PROJECT DETAILS</span>}
              {location.pathname.includes('/runhistory') && 'Run History'}
              {location.pathname.includes('/system-resources') && 'System Resources'}
              {location.pathname.includes('/organization') && 'Organizations'}
              {location.pathname.includes('/api-keys') && 'API Key Management'}
              {location.pathname.includes('/cost-analytics') && 'Cost Analytics'}
              {location.pathname.includes('/query-analytics') && 'Query Analytics'}
              {location.pathname.includes('/log-analytics') && 'Log Analytics'}
            </h1>
            <div className="flex items-center space-x-4">
              <OrganizationSwitcher 
                appearance={{
                  elements: {
                    organizationSwitcherTrigger: "flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors",
                    organizationSwitcherPopoverCard: "shadow-lg border border-gray-200",
                    organizationSwitcherPopoverActions: "p-2",
                    organizationSwitcherPopoverActionButton: "w-full text-left p-2 hover:bg-gray-100 rounded transition-colors"
                  }
                }}
              />
              <UserButton />
            </div>
          </div>
        )}

        {/* Mobile Page Title */}
        {isMobile && (
          <div className="bg-white border-b border-gray-200 px-4 py-3">
            <h1 className="text-lg font-semibold text-gray-700">
              {location.pathname.includes('/dashboard') && 'Dashboard'}
              {location.pathname === '/projects' && 'Projects'}
              {location.pathname.includes('/projects/') && !location.pathname.includes('/runhistory') && <span className="font-bold">PROJECT DETAILS</span>}
              {location.pathname.includes('/runhistory') && 'Run History'}
              {location.pathname.includes('/system-resources') && 'System Resources'}
              {location.pathname.includes('/organization') && 'Organizations'}
              {location.pathname.includes('/api-keys') && 'API Key Management'}
              {location.pathname.includes('/cost-analytics') && 'Cost Analytics'}
              {location.pathname.includes('/query-analytics') && 'Query Analytics'}
              {location.pathname.includes('/log-analytics') && 'Log Analytics'}
            </h1>
            <div className="mt-2">
              <OrganizationSwitcher 
                appearance={{
                  elements: {
                    organizationSwitcherTrigger: "flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors w-full",
                    organizationSwitcherPopoverCard: "shadow-lg border border-gray-200",
                    organizationSwitcherPopoverActions: "p-2",
                    organizationSwitcherPopoverActionButton: "w-full text-left p-2 hover:bg-gray-100 rounded transition-colors"
                  }
                }}
              />
            </div>
          </div>
        )}
        <main className={`flex-1 overflow-y-auto bg-gray-50 ${isMobile ? 'p-4' : isTablet ? 'p-5' : 'p-6'}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout; 